/**
 * The shapes the Brain API returns.
 *
 * Hand-written rather than generated, because these are the contract the whole
 * workspace reads: the canvas, the rail, the inspector and every sub-page all
 * render from `GraphModel`, and one place to look is worth the duplication.
 */

export type NodeCategory = 'core' | 'secondary' | 'derived'
export type Cardinality = '1:1' | '1:N' | 'N:1' | 'N:N'
export type DqKind = 'VALIDATE' | 'INFER' | 'COMPUTE' | 'ACCESS'
export type DqSeverity = 'ERROR' | 'WARN' | 'INFO'
export type SourceMode = 'direct_map' | 'extract'
export type SourceKind = 'structured' | 'unstructured'
export type RecordStatus = 'ACTIVE' | 'REVIEW' | 'FLAGGED' | 'MERGED_AWAY'

export const DATA_TYPES = [
  'string',
  'text',
  'number',
  'integer',
  'boolean',
  'date',
  'datetime',
  'enum',
  'email',
  'phone',
  'url',
  'json',
  'array',
] as const
export type DataType = (typeof DATA_TYPES)[number]

/**
 * What an array holds.
 *
 * `array` is a container rather than a type of its own, which is why the editor
 * shows one type control and a subtype beside it instead of four unrelated
 * `array<…>` entries a user has to hunt through.
 */
export const ARRAY_SUBTYPES = ['string', 'integer', 'decimal', 'boolean'] as const
export type ArraySubtype = (typeof ARRAY_SUBTYPES)[number]

export const CARDINALITIES: Cardinality[] = ['1:1', '1:N', 'N:1', 'N:N']
export const CATEGORIES: NodeCategory[] = ['core', 'secondary', 'derived']

export interface Graph {
  graph_id: string
  name: string
  description: string | null
  requirement: string | null
  status: string
  published_version: string | null
  created_at?: string | null
  updated_at?: string | null
  node_type_count?: number
  edge_type_count?: number
  record_count?: number
}

export interface PropertyDef {
  /** What an array holds. Null for every scalar type. */
  subtype?: string | null
  /** `array<string>` where the raw type would read `array`. */
  type_label?: string
  property_id: string
  node_type_id: string
  key: string
  display_name: string
  data_type: DataType
  description: string | null
  is_primary_key: boolean
  is_required: boolean
  is_indexed: boolean
  is_pii: boolean
  enum_values: string[]
  order_index: number
  flags: string[]
}

export interface EdgeTypeDef {
  edge_type_id: string
  graph_id: string
  key: string
  display_name: string
  description: string | null
  from_node_type_id: string
  to_node_type_id: string
  from_node_type: string
  to_node_type: string
  cardinality: Cardinality
  attributes: { key: string; data_type: DataType; enum_values?: string[] }[]
  order_index: number
  instance_count?: number
}

export interface NodeTypeDef {
  node_type_id: string
  graph_id: string
  key: string
  display_name: string
  category: NodeCategory
  description: string | null
  position: { x: number; y: number } | null
  order_index: number
  properties: PropertyDef[]
  edges_out: EdgeTypeDef[]
  edges_in: EdgeTypeDef[]
  property_count: number
  edge_count: number
  record_count?: number
  dq_rule_count?: number
  match_rule_count?: number
  survivorship_rule_count?: number
  updated_at?: string | null
}

export interface GraphModel {
  graph: Graph
  node_types: NodeTypeDef[]
  edge_types: EdgeTypeDef[]
}

/** The one call the node detail page loads — all five sub-pages in one shape. */
export interface NodeTypeDetail {
  node_type: Omit<NodeTypeDef, 'properties' | 'edges_out' | 'edges_in' | 'property_count' | 'edge_count'>
  properties: PropertyDef[]
  edges_out: EdgeTypeDef[]
  edges_in: EdgeTypeDef[]
  dq_rules: DqRule[]
  match_rules: MatchRule[]
  survivorship_rules: SurvivorshipRule[]
  counts: {
    properties: number
    edges: number
    dq_rules: number
    match_rules: number
    survivorship_rules: number
    records: number
  }
}

// --- rules -------------------------------------------------------------------

export interface DqRule {
  rule_id: string
  graph_id: string
  node_type_id: string
  key: string
  name: string
  description: string | null
  kind: DqKind
  severity: DqSeverity
  property_key: string | null
  definition: Record<string, unknown>
  active: boolean
  order_index: number
}

export interface DqOperatorArg {
  key: string
  label: string
  input: 'text' | 'number' | 'list' | 'property' | 'none'
  required: boolean
  help: string | null
}

export interface DqOperator {
  op: string
  label: string
  kind: DqKind
  summary: string
  takes_property: boolean
  args: DqOperatorArg[]
}

/** One comparison in a rule: a property, a test, and what it is tested against. */
export interface DqCondition {
  property: string
  op: string
  value?: unknown
}

/** The stored shape of a condition rule's `definition`. */
export interface DqDefinition {
  match: 'all' | 'any'
  action: 'accept' | 'reject'
  conditions: DqCondition[]
}

/**
 * What the condition builder renders from. `by_type` maps a property's declared
 * data type to the operators worth offering for it, so an integer is never
 * offered "contains" and a boolean is never offered "greater than".
 */
export interface DqConditionCatalogue {
  operators: { op: string; label: string; takes_value: boolean }[]
  by_type: Record<string, string[]>
  default: string[]
  actions: { action: 'accept' | 'reject'; label: string; summary: string }[]
}

export interface DqReport {
  score: number
  has_errors: boolean
  violations: {
    rule_id: string
    rule_key: string
    rule_name: string
    severity: DqSeverity
    property_key: string | null
    detail: string
  }[]
  passed: number
  derived: Record<string, unknown>
  access: { property_key: string; op: string; keep_last: number | null }[]
}

/**
 * How one field of two records is compared. `exact` answers yes or no;
 * `fuzzy` scores similarity between 0 and 1. The backend normalises the older
 * method names onto these two, so nothing else ever arrives here.
 */
export type MatchMethod = 'exact' | 'fuzzy'

/**
 * What a match is *for*, as opposed to how alike the pair is. A rule with any
 * fuzzy field can only be `relevance` — a fractional score is the only thing
 * such a rule produces — and the backend enforces that on write and on read.
 */
export type MatchAction = 'auto_merge' | 'review' | 'relevance'

export interface MatchField {
  property_key: string
  method: MatchMethod
}

export interface MatchRule {
  match_rule_id: string
  graph_id: string
  node_type_id: string
  key: string
  name: string
  description: string | null
  fields: MatchField[]
  action: MatchAction
  /** True when any field is fuzzy, so only the relevance action is offered. */
  is_fuzzy: boolean
  /**
   * Only read when `action` is `relevance`. Two independent spans of the 0–1
   * score line: [auto_merge_threshold, auto_merge_max] merges,
   * [review_threshold, review_max] is flagged for a person. Merge wins on
   * overlap.
   */
  auto_merge_threshold: number
  auto_merge_max: number
  review_threshold: number
  review_max: number
  /** Derived from the fields on the server, not configured here. */
  blocking_keys: string[]
  /** True for the primary-key rule: it is locked and cannot be deleted. */
  is_default?: boolean
  active: boolean
  order_index: number
}

export interface MatchMethodSpec {
  method: MatchMethod
  label: string
  summary: string
  options: { key: string; label: string; input: string; default?: unknown }[]
  /** True for `fuzzy`: the method scores rather than answering yes or no. */
  fuzzy: boolean
}

export interface MatchTestResult {
  match_rule_id: string
  key: string
  name: string
  active: boolean
  score: number
  verdict: 'auto_merge' | 'review' | 'distinct'
  /** How the rule reached that verdict, which the score alone does not say. */
  action: MatchAction
  auto_merge_threshold: number
  auto_merge_max: number
  review_threshold: number
  review_max: number
  signals: Record<
    string,
    { method?: string; value?: number | boolean; weight?: number; reason?: string }
  >
  blocks_left: string[]
  blocks_right: string[]
}

/**
 * The only two ways a surviving value is ever chosen. `recency` keeps the most
 * recent record; `source_system` keeps the value from the highest-ranked
 * source. The backend normalises the older strategy names onto these two, so
 * nothing else ever arrives here.
 */
export type SurvivorshipStrategy = 'recency' | 'source_system'

export interface SurvivorshipRule {
  survivorship_rule_id: string
  graph_id: string
  node_type_id: string
  /** `*` is the default rule; anything else is a field override. */
  property_key: string
  strategy: SurvivorshipStrategy
  /** Ordered source ids, best first. Only read by `source_system`. */
  source_priority: string[]
  min_confidence: number
  description: string | null
  active: boolean
  order_index: number
}

export interface StrategySpec {
  strategy: SurvivorshipStrategy
  label: string
  summary: string
  /** True for `source_system`, which is meaningless without a ranking. */
  needs_source_priority: boolean
}

export interface SurvivorshipTestResult {
  properties: Record<string, unknown>
  decisions: {
    field: string
    winning_value: unknown
    losing_values: unknown[]
    strategy: SurvivorshipStrategy
    source_id: string | null
    confidence: number | null
    detail: string | null
    /** True when a field override decided this, rather than the default rule. */
    from_field_override: boolean
  }[]
}

export interface MergeAuditRow {
  id: string
  entity_id: string
  action: string
  field: string | null
  winning_value: unknown
  losing_value: unknown
  strategy: string | null
  source_id: string | null
  confidence: number | null
  actor: string
  detail: string | null
  at: string | null
}

// --- records -----------------------------------------------------------------

export interface RecordRow {
  entity_id: string
  graph_id: string
  node_type_key: string
  label: string | null
  properties: Record<string, unknown>
  quality_score: number | null
  status: RecordStatus
  merged_into: string | null
  created_at: string | null
  updated_at: string | null
  sources?: { source_id: string | null; source_record_id: string | null; extracted_record_id: string }[]
  edge_count?: number
}

export interface StagedRecordRow {
  id: string
  staging_id: string
  chunk_index: number
  node_type_key: string
  temp_id: string
  properties: Record<string, unknown>
  edges: Record<string, unknown>[]
  confidence: number
  resolve_status: 'PENDING' | 'RESOLVED' | 'QUARANTINED'
  entity_id: string | null
  quarantine_reason: string | null
  model: string | null
  created_at: string | null
  /** The raw source row this record was extracted from, as the connector sent it. */
  source_payload?: Record<string, unknown> | null
}

export interface EgoNode {
  entity_id: string
  node_type_key: string
  /** The type's id, so a record is coloured the same here as on the model canvas. */
  node_type_id: string | null
  label: string | null
  category: NodeCategory
  /** Hops from the centre: 0 is the record itself, 1 its neighbours, and so on. */
  depth: number
  status: string
  quality_score: number | null
  properties: Record<string, unknown>
}

export interface EgoEdge {
  edge_id: string
  edge_type_key: string
  from_entity: string
  to_entity: string
  attributes: Record<string, unknown>
  confidence: number
}

export interface EgoGraph {
  center: EgoNode
  nodes: EgoNode[]
  edges: EgoEdge[]
  /** The depth that was walked, and the furthest the server will walk. */
  depth: number
  max_depth: number
  truncated: boolean
}

/** One connector at step one of the source flow. */
export interface SourceConnector {
  connector_id: string
  name: string
  description: string | null
  /** False when the connector has nothing a graph could read. */
  importable: boolean
  object_count: number
  connections: {
    connection_id: string
    label: string
    status: string
    created_at: string | null
  }[]
}

/** What one connection can be asked for — its tables, folders, lookups. */
export interface ConnectionObjects {
  connection_id: string
  connector_id: string
  connector_name: string
  label: string
  suggested_kind: SourceKind
  suggested_mode: SourceMode
  objects: ImportScopeOption[]
}

/** A few real rows, so a mapping is built against what the source sends. */
export interface ObjectPreview {
  ok: boolean
  error: string | null
  columns: { name: string; detected_type: string }[]
  rows: Record<string, unknown>[]
  total_seen?: number
}

export interface ExplorerStats {
  /** Whether a graph store is configured. The counts are real either way. */
  configured: boolean
  /** Which engine produced these counts — `sql` or `graph`. */
  engine?: CypherEngine
  labels: { label: string; count: number }[]
  relationship_types: { type: string; count: number }[]
  node_count: number
  relationship_count: number
  headroom: {
    node_cap: number
    relationship_cap: number
    nodes_used: number
    relationships_used: number
    nodes_left: number
    relationships_left: number
  }
  source?: string
  cached: boolean
}

/**
 * Which engine answers a Cypher query.
 *
 * `sql` runs an openCypher subset directly against the records, needs no
 * external service, and is never stale. `graph` is the full language against
 * the Neo4j projection. `auto` takes the store when there is one.
 */
export type CypherEngine = 'auto' | 'sql' | 'graph'

export interface CypherResult {
  ok: boolean
  /** Which engine actually ran it. */
  engine?: CypherEngine
  fields: string[]
  rows: unknown[][]
  error?: string
  code?: string
  duration_ms?: number
  limit?: number
  limit_applied?: boolean
  row_count?: number
  refused_before_running?: boolean
  rate_limited?: boolean
}

export interface SavedQuery {
  query_id: string
  name: string
  description: string | null
  cypher: string
  created_at: string | null
}

export interface QueryHistoryEntry {
  entry_id: string
  cypher: string
  ok: boolean
  row_count: number
  duration_ms: number
  error: string | null
  at: string | null
}

export interface QueryLibrary {
  presets: { name: string; description: string; cypher: string }[]
  saved: SavedQuery[]
  history: QueryHistoryEntry[]
}

export interface ExplorerLimits {
  row_cap: number
  timeout_s: number
  read_only: boolean
  forbidden_clauses: string[]
  allowed_procedures: string[]
  engines?: {
    id: CypherEngine
    label: string
    available: boolean
    description: string
    max_nodes?: number
    max_relationships?: number
    max_hops?: number
  }[]
  default_engine?: CypherEngine
}

// --- relationships between records -------------------------------------------

/** One end of a relationship: enough to render it and link to the record. */
export interface RelationshipEnd {
  entity_id: string
  node_type_key: string
  label: string | null
  status: string
}

export interface RelationshipRow {
  edge_id: string
  edge_type_key: string
  confidence: number
  attributes: Record<string, unknown>
  /** Why the graph believes it — which column produced it, or which chunk. */
  provenance: Record<string, unknown>[]
  created_at: string | null
  from: RelationshipEnd
  to: RelationshipEnd
}

export interface RelationshipPage {
  relationships: RelationshipRow[]
  meta: { page: number; per_page: number; total: number }
}

export interface RelationshipSummary {
  /** The type pairs records are *actually* related across. */
  pairs: {
    edge_type_key: string
    from_node_type: string
    to_node_type: string
    count: number
  }[]
  edge_types: {
    edge_type_id: string
    key: string
    from_node_type: string | null
    to_node_type: string | null
    cardinality: string
    instance_count: number
  }[]
  total: number
  unconnected_records: number
}

/** Per declared link: did it resolve, and what did it fail to match? */
export interface LinkReportRow {
  node_type_key: string
  property_key: string
  edge_type_key: string
  to_node_type: string
  to_property: string
  reversed: boolean
  separator: string | null
  records: number
  records_with_value: number
  records_resolved: number
  records_unresolved: number
  edges: number
  unmatched_examples: string[]
}

export interface LinkReport {
  links: LinkReportRow[]
  /** Declarations the model cannot honour, with the reason. */
  skipped: { node_type: string; property_key: string; edge_type_key: string; reason: string }[]
  edges: number
}

export interface RecordRelationships {
  record: RecordRow
  outgoing: {
    edge_id: string
    edge_type_key: string
    other: RelationshipEnd
    provenance: Record<string, unknown>[]
  }[]
  incoming: {
    edge_id: string
    edge_type_key: string
    other: RelationshipEnd
    provenance: Record<string, unknown>[]
  }[]
  total: number
}

/** Every count the Records screen shows, in one payload. */
export interface RecordCounts {
  staging: {
    new: number
    extracted: number
    processed: number
    failed: number
    total: number
    by_status: Record<string, number>
  }
  staged_records: {
    pending: number
    resolved: number
    rejected: number
    total: number
    by_status: Record<string, number>
  }
  matches: { potential: number; merged: number; rejected: number; total: number }
  records: {
    golden: number
    active: number
    review: number
    flagged: number
    merged_away: number
    by_type: Record<string, number>
  }
  relationships: {
    total: number
    by_type: Record<string, number>
    unconnected_records: number
  }
  queues: Record<string, number>
}
export interface DemoStatus {
  installed: boolean
  graph_id: string | null
  name: string
  description: string
  contents: { node_types: number; edge_types: number; sources: number; rows: number }
}

export interface DemoInstalled {
  graph_id: string
  name: string
  node_types: number
  edge_types: number
  sources: number
  rules: number
  rows: number
  edges: number
  records: number
  records_by_type: Record<string, number>
  quarantined: number
  awaiting_review: number
}

export interface Lineage {
  entity_id: string
  sources: { source_id: string | null; source_record_id: string | null; extracted_record_id: string; at: string | null }[]
  audit: MergeAuditRow[]
}

export interface ReviewPair {
  review_id: string
  node_type_id: string
  entity_a: string
  entity_b: string
  score: number
  signals: Record<string, unknown>
  status: string
  note: string | null
  resolved_by: string | null
  resolved_at: string | null
  created_at: string | null
  candidate_a: RecordRow | null
  candidate_b: RecordRow | null
}

// --- sources, agents, pipeline ----------------------------------------------

/** A column that points at another record instead of describing this one. */
export interface SourceLink {
  property_key: string
  edge_type_key: string
  to_node_type: string
  to_property: string
  /**
   * Set when the column holds several keys in one string — `"1,2,3"`.
   *
   * A list-valued column needs nothing here: a list is already many values.
   * Splitting a plain string is a guess, so it only happens when asked.
   */
  separator?: string | null
}

/** How a source keeps up with what it reads. */
export type SyncMode = 'batch' | 'poll'

/** One object a source reads, and what its rows become. */
export interface SourceObject {
  action_id: string
  label: string | null
  input: Record<string, unknown>
  node_type_key: string | null
  mapping: Record<string, string>
  links: SourceLink[]
}

export interface BrainSource {
  id: string
  graph_id: string
  source_id: string
  connection_id: string | null
  connector_id: string
  display_name: string
  kind: SourceKind
  mode: SourceMode
  /**
   * `batch` publishes when asked. `poll` is re-read on a timer, so a row
   * created in the source after the last publish arrives on its own.
   *
   * Not a webhook and not named like one: a provider that does not call us
   * cannot be made to, and half the connectors here are databases with nothing
   * to call us *with*. The interval decides the latency.
   */
  sync_mode: SyncMode
  /** Seconds between passes when polling. Null on a batch source. */
  poll_interval_s: number | null
  /** When the poller may next touch it. Null means "at the next tick". */
  next_poll_at: string | null
  entity_hint: string | null
  ingest_config: Record<string, unknown>
  mapping: Record<string, string>
  /** What it reads. A connection is the source; these are the tables in it. */
  objects: SourceObject[]
  watermark: Record<string, unknown>
  /** Where each object's sync stands — one entry per object it reads. */
  sync: SourceSync[]
  last_run_status: string
  last_run_at: string | null
  last_error: string | null
  active: boolean
  staged_count: number
}

/**
 * How one object keeps up, and how far it has got.
 *
 * `strategy` is how new rows are found: `filter` asks the provider for rows
 * changed since the high-water mark, `newest_first` walks newest-first pages
 * until it reaches rows already seen, `sweep` re-walks the whole collection a
 * page budget at a time, `whole` re-reads a single request every pass.
 */
export interface SourceSync {
  key: string
  action_id: string
  label: string
  strategy: 'filter' | 'newest_first' | 'sweep' | 'whole'
  paginated: boolean
  /** Everything up to here has been read. Null before the first pass. */
  high_water: string | null
  /** A forward pass that ran out of budget mid-delta; carried over to the next. */
  forward: Record<string, unknown> | null
  /** Where the backfill resumes. Null before the first pass and after the last. */
  cursor: Record<string, unknown> | null
  backfill_done: boolean
  sweeps: number
  pages: number
  seen: number
  last_pass_at: string | null
}

export interface AvailableConnection {
  connection_id: string
  connector_id: string
  label: string
  status: string
  suggested_kind: SourceKind
  suggested_mode: SourceMode
}

export interface Agent {
  agent_id: string
  graph_id: string
  key: string
  display_name: string
  description: string | null
  mode: SourceMode
  model: string
  prompt_version: string
  node_type_keys: string[]
  instructions: string | null
  eval_precision: number | null
  eval_recall: number | null
  runs: number
  failures: number
  records_produced: number
  last_error: string | null
  active: boolean
  status: 'ok' | 'idle' | 'error'
}

export interface ProviderStatus {
  llm: {
    configured: boolean
    default_model: string | null
    fallback_model: string | null
    providers: string[]
  }
  embeddings: { configured: boolean; model: string; dimensions: number }
}

export interface PipelineStatus {
  queues: Record<string, number>
  staging: Record<string, number>
  extracted: Record<string, number>
  archived: { id: string; queue: string; error: string | null; read_ct: number; at: string | null }[]
}

export interface PipelineRun {
  run_id: string
  trigger: string
  counts: Record<string, { handled: number; produced: number; failed: number }>
  duration_ms: number
  status: string
  error: string | null
  at: string | null
}

export interface GraphStats {
  node_types: number
  edge_types: number
  records: number
  records_by_type: Record<string, number>
  edges_by_type: Record<string, number>
  pending_reviews: number
  queues: Record<string, number>
}

// --- objects -----------------------------------------------------------------

export interface ObjectColumn {
  property_key: string
  column_name: string
  data_type: DataType
}

export interface ObjectTable {
  object_table_id: string
  graph_id: string
  node_type_id: string
  node_type_key: string
  schema_name: string | null
  table_name: string
  /** What you would type in a SQL client. */
  qualified_name: string
  columns: ObjectColumn[]
  row_count: number
  exists: boolean
  last_error: string | null
}

/** A row carries the system columns plus one per property. */
export type ObjectRow = Record<string, unknown> & {
  object_id: string
  entity_id: string | null
  graph_id: string
  node_type_id: string
  node_type_key: string
  label: string | null
  status: string
  quality_score: number | null
  source_ids: string[] | null
  created_at: string | null
  updated_at: string | null
}

export interface ObjectRows {
  table: ObjectTable
  rows: ObjectRow[]
  meta: { page: number; per_page: number; total: number }
}

// --- search and ask ----------------------------------------------------------

export interface SearchHit {
  kind: 'entity' | 'chunk'
  ref_id: string
  title: string
  text: string
  score: number
  vector_rank: number | null
  keyword_rank: number | null
  node_type_key?: string
  staging_id?: string
}

export interface AskSource {
  index: number
  kind: string
  ref_id: string
  title: string
  excerpt: string
  node_type_key?: string
  status?: string
  staging_id?: string
}

// --- generator ---------------------------------------------------------------

export interface ProposedProperty {
  key: string
  data_type: DataType
  description?: string | null
  is_primary_key: boolean
  is_required: boolean
  is_indexed: boolean
  is_pii: boolean
  enum_values: string[]
}

export interface ModelProposal {
  name?: string | null
  description?: string | null
  summary?: string | null
  node_types: {
    key: string
    category: NodeCategory
    description?: string | null
    properties: ProposedProperty[]
  }[]
  edge_types: {
    key: string
    from_node_type: string
    to_node_type: string
    cardinality: Cardinality
    description?: string | null
    attributes: { key: string; data_type: DataType }[]
  }[]
  dq_rules: {
    node_type: string
    key: string
    name: string
    kind: DqKind
    severity: DqSeverity
    property_key: string | null
    definition: Record<string, unknown>
  }[]
  match_rules: {
    node_type: string
    key: string
    name: string
    blocking_keys: string[]
    comparators: { property_key: string; method: string; weight: number }[]
    auto_merge_threshold: number
    review_threshold: number
  }[]
  survivorship_rules: {
    node_type: string
    property_key: string
    strategy: SurvivorshipStrategy
    source_priority: string[]
  }[]
  routing: { source_id: string; mode: string; entity_hint: string | null; rationale: string | null }[]
}

export interface DraftResult {
  proposal: ModelProposal
  generated_by: {
    model: string
    provider: string
    prompt_version: string
    latency_ms: number
    attempts: number
  }
}

export interface ApplyResult {
  graph_id: string
  summary: string | null
  applied: {
    node_types: number
    properties: number
    edge_types: number
    dq_rules: number
    match_rules: number
    survivorship_rules: number
    routed_sources: string[]
    skipped: { what: string; key: string; why: string }[]
  }
  generated_by: Record<string, unknown>
}


/**
 * One thing a source can be asked to import, as the Sources tab shows it. The
 * label is the user's word for it ("Pages", "Blog posts"), not the action id,
 * and the whole list comes from the connector's manifest — a new connector
 * appears here with no frontend change.
 */
export interface ImportScopeOption {
  action_id: string
  action_name: string
  label: string
  summary: string
  kind: 'structured' | 'unstructured'
  text_keys: string[]
  id_key: string | null
  /** Ticked when the picker first opens. */
  default: boolean
  /** The action's own inputs, for a scope that needs a space or a channel. */
  input_schema: {
    type?: string
    required?: string[]
    properties?: Record<string, Record<string, unknown>>
  }
}

export interface ImportResult {
  /**
   * False when the provider refused or was unreachable. Returned rather than
   * thrown so a multi-scope import keeps the scopes that did work, and so the
   * failure is still recorded on the source row.
   */
  ok: boolean
  error: string | null
  received: number
  staged: number
  duplicates: number
  action_id: string
  label: string
  pages: number
}
